import { match } from 'ts-pattern';

import {
  FiatACHPaymentForm,
  FiatCOPBeneficiaryCompanyPaymentForm,
  FiatCOPPaymentForm,
  FiatCOPThirdPartyCompanyPaymentForm,
  FiatCvuPaymentForm,
  FiatPaymentForm,
  FiatPixPaymentForm,
  FiatSpeiPaymentForm,
  FiatSwiftPaymentForm,
  FiatWirePaymentForm,
  isFiatACHPaymentForm,
  isFiatCOPBeneficiaryPaymentForm,
  isFiatCOPPaymentForm,
  isFiatCOPThirdPartyPaymentForm,
  isFiatCvuPaymentForm,
  isFiatPixPaymentForm,
  isFiatSpeiPaymentForm,
  isFiatSwiftPaymentForm,
  isFiatWirePaymentForm,
} from '@/schemas/paymentMethod';
import { BankAccountType } from '@/types/bankAccountType';
import {
  isACHAccountDetails,
  isCompanyCheckingACHUserInfo,
  isCompanyContact,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isIndividualCheckingACHUserInfo,
  isIndividualContact,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';
import { CopDocumentType } from '@/types/copDocumentType';
import { CountryType, toCountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { Customer } from '@/types/customer';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import { PaymentMethodAccountType } from '@/types/paymentMethodAccountType';
import { RecipientResponse } from '@/types/recipient';
import { CryptoCurrencyCodeType } from '@/types/simulation';
import {
  getRecipientAccountNumber,
  getRecipientBankName,
  getRecipientCurrency,
} from '@/utility/recipientDisplayAdapter';

export enum ContactType {
  individual = 'NATURAL_PERSON',
  company = 'COMPANY',
}

export interface IndividualContact {
  readonly type: ContactType.individual;
  readonly firstName: string;
  readonly lastName: string;
  readonly businessRelationship: BusinessRelationshipType;
}

export interface CompanyContact {
  readonly type: ContactType.company;
  readonly legalEntityName: string;
  readonly businessRelationship: BusinessRelationshipType;
}

export interface ContactAddress {
  readonly street: string;
  readonly complement: string;
  readonly city: string;
  readonly state: string | null;
  readonly zipCode: string;
}

export interface BeneficiaryAccountDetails {
  readonly address: ContactAddress;
  readonly legalEntityName: string;
  readonly country: CountryType;
}

export class ContactAddress {
  public static empty(): ContactAddress {
    return {
      street: '',
      complement: '',
      city: '',
      state: '',
      zipCode: '',
    };
  }
}

export class BeneficiaryAccountDetails {
  public static empty(): BeneficiaryAccountDetails {
    return {
      address: ContactAddress.empty(),
      legalEntityName: '',
      country: CountryType.unset,
    };
  }
}

export type ContactData = IndividualContact | CompanyContact;

export type TranslatableFiatAccountType =
  | ''
  | 'FIAT_ACCOUNT_ACH'
  | 'FIAT_ACCOUNT_WIRE'
  | 'FIAT_ACCOUNT_RTP'
  | 'FIAT_ACCOUNT_SWIFT'
  | 'FIAT_ACCOUNT_CNAPS'
  | 'FIAT_ACCOUNT_CHATS'
  | 'FIAT_ACCOUNT_SEPA'
  | 'FIAT_ACCOUNT_PIX'
  | 'FIAT_ACCOUNT_COP'
  | 'FIAT_ACCOUNT_SPEI'
  | 'FIAT_ACCOUNT_CVU'
  | 'FIAT_ACCOUNT_CRYPTO'
  // Thunes local corridors (caliza-core-api PR #3411)
  | 'FIAT_ACCOUNT_FASTER_PAYMENTS'
  | 'FIAT_ACCOUNT_IMPS'
  | 'FIAT_ACCOUNT_INSTAPAY'
  | 'FIAT_ACCOUNT_RTC'
  | 'FIAT_ACCOUNT_PROMPTPAY'
  | 'FIAT_ACCOUNT_BI_FAST'
  | 'FIAT_ACCOUNT_GHIPSS'
  | 'FIAT_ACCOUNT_PESALINK'
  | 'FIAT_ACCOUNT_FAST'
  | 'FIAT_ACCOUNT_EFT'
  | 'FIAT_ACCOUNT_TEF'
  | 'FIAT_ACCOUNT_NIP'
  | 'FIAT_ACCOUNT_CCE'
  | 'FIAT_ACCOUNT_IPP'
  | 'FIAT_ACCOUNT_BOB_ACH'
  | 'FIAT_ACCOUNT_MOBILE_WALLET';

export interface WireUserInfo {
  readonly name: string;
  readonly street1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly routingKey: string;
  readonly accountNumber: string;
  readonly accountType: BankAccountType;
  readonly type: ContactType;
}

export interface WireBankInfo {
  readonly bic: string;
  readonly name: string;
  readonly street1: string;
  readonly street2: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
}

export interface WireBusinessInfo {
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly postalCode: string;
  readonly registrationNumber: string | null;
  readonly accountNumber: string;
  readonly accountType: BankAccountType;
  readonly routingNumber: string;
  readonly phoneNumber?: string | null;
  readonly registrationType?: string | null;
  readonly registrationDate?: string | null;
  readonly registrationExpiryDate?: string | null;
  readonly mobileNumber?: string | null;
  readonly businessNature?: string | null;
  readonly email?: string | null;
}

export interface WireAccount {
  readonly personInfo: WireUserInfo | null;
  readonly businessInfo: WireBusinessInfo | null;
  readonly bankInfo: WireBankInfo;
}

export interface ACHUserInfoBase {
  readonly name: string;
  readonly street1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly routingKey: string;
  readonly accountNumber: string;
  readonly accountType: BankAccountType;
  readonly type: ContactType;
}

export interface CompanyCheckingACHUserInfo extends ACHUserInfoBase {
  readonly accountType: BankAccountType.checking;
  readonly type: ContactType.company;
}

export interface IndividualCheckingACHUserInfo extends ACHUserInfoBase {
  readonly accountType: BankAccountType.checking;
  readonly type: ContactType.individual;
}

export interface CompanySavingsACHUserInfo extends ACHUserInfoBase {
  readonly accountType: BankAccountType.savings;
  readonly type: ContactType.company;
}

export interface IndividualSavingsACHUserInfo extends ACHUserInfoBase {
  readonly accountType: BankAccountType.savings;
  readonly type: ContactType.individual;
}

export type ACHUserInfo = CompanyCheckingACHUserInfo | IndividualCheckingACHUserInfo;

export const isACHUserInfo = (something: unknown): something is ACHUserInfo =>
  isCompanyCheckingACHUserInfo(something) || isIndividualCheckingACHUserInfo(something);

export const isACHUserInfoOrNull = (something: unknown): something is ACHUserInfo | null =>
  something === null || isACHUserInfo(something);

export interface ACHBankInfo {
  readonly routingNumber: string;
  readonly name: string;
  readonly street1: string;
  readonly street2: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
}

export interface ACHBusinessInfo {
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly postalCode: string;
  readonly registrationNumber: string | null;
  readonly accountNumber: string;
  readonly accountType: BankAccountType;
  readonly routingNumber: string;
  readonly phoneNumber?: string | null;
  readonly registrationType?: string | null;
  readonly registrationDate?: string | null;
  readonly registrationExpiryDate?: string | null;
  readonly mobileNumber?: string | null;
  readonly businessNature?: string | null;
  readonly email?: string | null;
}

export interface ACHAccount {
  readonly personInfo: ACHUserInfo | null;
  readonly businessInfo: ACHBusinessInfo | null;
  readonly bankInfo: ACHBankInfo;
}

export interface SwiftUserInfo {
  readonly name: string;
  readonly street1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly routingKey: string;
  readonly iban: string;
  readonly accountType: BankAccountType;
  readonly type: ContactType;
}

export interface SwiftBusinessInfo {
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
  readonly postalCode: string;
  readonly registrationNumber: string | null;
  readonly accountNumber: string;
  readonly accountType: BankAccountType;
  readonly routingNumber: string;
  readonly phoneNumber?: string | null;
  readonly registrationType?: string | null;
  readonly registrationDate?: string | null;
  readonly registrationExpiryDate?: string | null;
  readonly mobileNumber?: string | null;
  readonly businessNature?: string | null;
  readonly email?: string | null;
}

export interface SwiftBankInfo {
  readonly swiftCode: string;
  readonly name: string;
  readonly street1: string;
  readonly street2: string;
  readonly postalCode: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
}

export interface SwiftAccount {
  readonly personInfo: SwiftUserInfo | null;
  readonly businessInfo: SwiftBusinessInfo | null;
  readonly bankInfo: SwiftBankInfo;
  readonly isPobo: boolean | null;
}

export interface PixAccount {
  readonly pixKey: string;
}

export interface SpeiAccount {
  readonly clabe: string;
  readonly bankCode: string;
  readonly receiverName: string;
}

export interface COPAccount {
  readonly firstName: string;
  readonly lastName?: string | null;
  readonly documentType: CopDocumentType | null;
  readonly accountType: number;
  readonly bankAccount: string;
  readonly bankCode: string;
  readonly thirdPartyWithdrawal: boolean | null;
  readonly documentId: string | null;
  readonly email: string | null;
}

export interface CVUAccount {
  readonly cvu: string;
  readonly recipientName: string;
}

export interface WireAccountDetails {
  readonly wireDetails: WireAccount;
}

export interface ACHAccountDetails {
  readonly achDetails: ACHAccount;
}

export interface SwiftAccountDetails {
  readonly swiftDetails: SwiftAccount;
}

export interface PIXAccountDetails {
  readonly pixDetails: PixAccount;
}

export interface COPAccountDetails {
  readonly copDetails: COPAccount;
}

export interface SpeiAccountDetails {
  readonly speiDetails: SpeiAccount;
}

export interface CVUAccountDetails {
  readonly cvuDetails: CVUAccount;
}

export type AccountDetails =
  | WireAccountDetails
  | ACHAccountDetails
  | SwiftAccountDetails
  | PIXAccountDetails
  | COPAccountDetails
  | SpeiAccountDetails
  | CVUAccountDetails;

export interface CreateFiatAccountResponse {
  readonly id: string;
  readonly name: string;
  readonly beneficiaryId: string;
  readonly belongsToBeneficiary: boolean;
  readonly fiatAccountType: FiatAccountType;
  readonly accountDetails: AccountDetails;
}

export interface CreateFiatAccountRequest {
  readonly name: string;
  readonly beneficiaryId: string;
  readonly belongsToBeneficiary: boolean;
  readonly fiatAccountType: FiatAccountType;
  readonly accountDetails: AccountDetails;
  readonly contactId: string | null;
  readonly nickname?: string | null;
}

export interface UpdateFiatAccountRequestData {
  readonly name?: string;
  readonly nickname?: string | null;
}

export interface UpdateFiatAccountRequest {
  readonly id: string;
  readonly data: UpdateFiatAccountRequestData;
}

const createPIXAccountDetails = (paymentMethodForm: FiatPixPaymentForm): PIXAccountDetails => ({
  pixDetails: {
    pixKey: paymentMethodForm.pixKey,
  },
});

const createSpeiAccountDetails = (paymentMethodForm: FiatSpeiPaymentForm): SpeiAccountDetails => ({
  speiDetails: {
    clabe: paymentMethodForm.clabe,
    bankCode: paymentMethodForm.bankCode,
    receiverName: paymentMethodForm.receiverName,
  },
});

const createCVUAccountDetails = (paymentMethodForm: FiatCvuPaymentForm): CVUAccountDetails => ({
  cvuDetails: {
    cvu: paymentMethodForm.cvu,
    recipientName: paymentMethodForm.recipientName,
  },
});

const createACHAccount = (
  contact: Contact | SelfContact,
  paymentMethodForm: FiatACHPaymentForm,
  bankAddress: ContactAddress,
): ACHAccountDetails => {
  const contactType = getFiatAccountContactType(contact);

  const personInfo: ACHUserInfo | null =
    contactType === ContactType.individual
      ? {
          name: getFiatAccountLegalName(contact),
          street1: contact.address.street,
          postalCode: contact.address.zipCode,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          routingKey: paymentMethodForm.routingNumber,
          accountNumber: paymentMethodForm.accountNumber,
          accountType: BankAccountType.checking,
          type: contactType,
        }
      : null;

  const businessInfo: ACHBusinessInfo | null =
    contactType === ContactType.company
      ? {
          name: getFiatAccountLegalName(contact),
          address: contact.address.street,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          postalCode: contact.address.zipCode,
          registrationNumber: null,
          accountNumber: paymentMethodForm.accountNumber,
          accountType: BankAccountType.checking,
          routingNumber: paymentMethodForm.routingNumber,
        }
      : null;

  return {
    achDetails: {
      personInfo,
      businessInfo,
      bankInfo: {
        routingNumber: paymentMethodForm.routingNumber,
        name: paymentMethodForm.bankName,
        street1: bankAddress.street,
        street2: bankAddress.complement,
        postalCode: bankAddress.zipCode,
        city: bankAddress.city,
        state: bankAddress.state,
        country: paymentMethodForm.country,
      },
    },
  };
};

const createWireAccount = (
  contact: Contact | SelfContact,
  paymentMethodForm: FiatWirePaymentForm,
  bankAddress: ContactAddress,
): WireAccountDetails => {
  const contactType = getFiatAccountContactType(contact);

  const personInfo: WireUserInfo | null =
    contactType === ContactType.individual
      ? {
          name: getFiatAccountLegalName(contact),
          street1: contact.address.street,
          postalCode: contact.address.zipCode,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          routingKey: paymentMethodForm.routingNumber,
          accountNumber: paymentMethodForm.accountNumber,
          accountType: BankAccountType.checking,
          type: contactType,
        }
      : null;

  const businessInfo: WireBusinessInfo | null =
    contactType === ContactType.company
      ? {
          name: getFiatAccountLegalName(contact),
          address: contact.address.street,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          postalCode: contact.address.zipCode,
          registrationNumber: null,
          accountNumber: paymentMethodForm.accountNumber,
          accountType: BankAccountType.checking,
          routingNumber: paymentMethodForm.routingNumber,
        }
      : null;

  return {
    wireDetails: {
      personInfo,
      businessInfo,
      bankInfo: {
        bic: paymentMethodForm.routingNumber,
        name: paymentMethodForm.bankName,
        street1: bankAddress.street,
        street2: bankAddress.complement,
        postalCode: bankAddress.zipCode,
        city: bankAddress.city,
        state: bankAddress.state,
        country: paymentMethodForm.country,
      },
    },
  };
};

const createSwiftAccount = (
  contact: Contact | SelfContact,
  paymentMethodForm: FiatSwiftPaymentForm,
  bankAddress: ContactAddress,
): SwiftAccountDetails => {
  const contactType = getFiatAccountContactType(contact);

  const personInfo: SwiftUserInfo | null =
    contactType === ContactType.individual
      ? {
          name: getFiatAccountLegalName(contact),
          street1: contact.address.street,
          postalCode: contact.address.zipCode,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          routingKey: paymentMethodForm.swiftCode,
          iban: paymentMethodForm.iban,
          accountType: BankAccountType.checking,
          type: contactType,
        }
      : null;

  const businessInfo: SwiftBusinessInfo | null =
    contactType === ContactType.company
      ? {
          name: getFiatAccountLegalName(contact),
          address: contact.address.street,
          city: contact.address.city,
          state: contact.address.state,
          country: contact.country,
          postalCode: contact.address.zipCode,
          registrationNumber: paymentMethodForm.registrationNumber || '',
          accountNumber: paymentMethodForm.iban,
          accountType: BankAccountType.checking,
          routingNumber: paymentMethodForm.swiftCode,
        }
      : null;

  return {
    swiftDetails: {
      personInfo,
      businessInfo,
      bankInfo: {
        swiftCode: paymentMethodForm.swiftCode,
        name: paymentMethodForm.bankName,
        street1: bankAddress.street,
        street2: bankAddress.complement,
        postalCode: bankAddress.zipCode,
        city: bankAddress.city,
        state: bankAddress.state,
        country: paymentMethodForm.country,
      },
      isPobo: paymentMethodForm.isPobo,
    },
  };
};

const createCOPAccount = (paymentMethodForm: FiatCOPPaymentForm): COPAccountDetails =>
  match<FiatCOPPaymentForm, COPAccountDetails>(paymentMethodForm)
    .when(
      isFiatCOPThirdPartyPaymentForm,
      (form: FiatCOPThirdPartyCompanyPaymentForm): COPAccountDetails => ({
        copDetails: {
          firstName: form.firstName,
          accountType: form.bankAccount === BankAccountType.checking ? 1 : 2,
          bankAccount: form.bankAccount,
          bankCode: form.bankCode,
          thirdPartyWithdrawal: form.thirdPartyWithdrawal,
          documentId: form.documentId,
          documentType: form.documentType,
          email: form.email,
        },
      }),
    )
    .when(
      isFiatCOPBeneficiaryPaymentForm,
      (form: FiatCOPBeneficiaryCompanyPaymentForm): COPAccountDetails => ({
        copDetails: {
          firstName: form.firstName,
          accountType: form.bankAccount === BankAccountType.checking ? 1 : 2,
          bankAccount: form.bankAccount,
          bankCode: form.bankCode,
          thirdPartyWithdrawal: false,
          documentId: null,
          documentType: null,
          email: null,
        },
      }),
    )
    .exhaustive();

const getFiatAccountLegalName = (contact: Contact | SelfContact): string => {
  return match<ContactData, string>(contact.entity)
    .when(
      isIndividualContact,
      (individualContact: IndividualContact): string =>
        `${individualContact.firstName} ${individualContact.lastName}`,
    )
    .when(
      isCompanyContact,
      (companyContact: CompanyContact): string => companyContact.legalEntityName,
    )
    .exhaustive();
};

const getFiatAccountContactType = (contact: Contact | SelfContact): ContactType => {
  return match<ContactData, ContactType>(contact.entity)
    .when(isIndividualContact, (): ContactType => ContactType.individual)
    .when(isCompanyContact, (): ContactType => ContactType.company)
    .exhaustive();
};

const getAccountDetails = (
  contact: Contact | SelfContact,
  paymentMethodForm: FiatPaymentForm,
): AccountDetails => {
  const { address } = { address: ContactAddress.empty(), ...paymentMethodForm };

  return match<FiatPaymentForm, AccountDetails>(paymentMethodForm)
    .when(
      isFiatPixPaymentForm,
      (form: FiatPixPaymentForm): AccountDetails => createPIXAccountDetails(form),
    )
    .when(
      isFiatWirePaymentForm,
      (form: FiatWirePaymentForm): AccountDetails => createWireAccount(contact, form, address),
    )
    .when(
      isFiatACHPaymentForm,
      (form: FiatACHPaymentForm): AccountDetails => createACHAccount(contact, form, address),
    )
    .when(
      isFiatSwiftPaymentForm,
      (form: FiatSwiftPaymentForm): AccountDetails => createSwiftAccount(contact, form, address),
    )
    .when(
      isFiatCOPPaymentForm,
      (form: FiatCOPPaymentForm): AccountDetails => createCOPAccount(form),
    )
    .when(
      isFiatSpeiPaymentForm,
      (form: FiatSpeiPaymentForm): AccountDetails => createSpeiAccountDetails(form),
    )
    .when(
      isFiatCvuPaymentForm,
      (form: FiatCvuPaymentForm): AccountDetails => createCVUAccountDetails(form),
    )
    .otherwise((form: any): AccountDetails => {
      console.debug(form);
      return {} as AccountDetails;
    });
};

export class CreateFiatAccountRequest {
  public static build(
    beneficiaryId: string,
    contact: Contact | SelfContact,
    paymentMethodForm: FiatPaymentForm,
    nickname?: string | null,
  ): CreateFiatAccountRequest {
    return {
      name: getFiatAccountLegalName(contact),
      beneficiaryId: beneficiaryId,
      belongsToBeneficiary: contact.id === null,
      fiatAccountType: paymentMethodForm.paymentMethod,
      accountDetails: getAccountDetails(contact, paymentMethodForm),
      contactId: contact.id,
      nickname: nickname?.trim() || null,
    } as CreateFiatAccountRequest;
  }
}

export interface RecipientAccount {
  readonly id: string;
  readonly name: string;
  readonly bankName: string | null;
  readonly accountNumber: string | null;
  readonly paymentMethod: string;
  readonly currency: string;
  readonly currencyCode: CurrencyCodeType;
  readonly nickname: string | null;
}

export interface CreateContactRequest {
  readonly beneficiaryId: string;
  readonly entity: ContactData;
  readonly country: CountryType;
  readonly address: ContactAddress;
  readonly belongsToBeneficiary: boolean;
}

export interface UpdateContactRequestData {
  readonly businessRelationship: BusinessRelationshipType;
}

export interface UpdateContactRequest {
  readonly id: string;
  readonly data: UpdateContactRequestData;
}

export interface CryptoAccount {
  readonly id: string;
  readonly name: string;
  readonly currency: CryptoCurrencyCodeType;
  readonly chain: string;
  readonly walletAddress: string;
}

export interface FiatAccount {
  readonly id: string;
  readonly name: string;
  readonly beneficiaryId: string;
  readonly belongsToBeneficiary: boolean;
  readonly fiatAccountType: FiatAccountType;
  readonly accountDetails: AccountDetails;
  readonly nickname: string | null;
}

export type Account = FiatAccount | CryptoAccount;

export const isFiatAccount = (something: unknown): something is FiatAccount =>
  typeof something === 'object' &&
  something !== null &&
  'id' in something &&
  'name' in something &&
  'beneficiaryId' in something &&
  'belongsToBeneficiary' in something &&
  'fiatAccountType' in something &&
  'accountDetails' in something;

export const calizaWalletUSDC: CryptoAccount = {
  id: 'CALIZA_WALLET',
  name: 'CALIZA_WALLET',
  currency: CryptoCurrencyCodeType.USDC,
  chain: 'Polygon',
  walletAddress: '',
};

export const calizaWalletUSDT: CryptoAccount = {
  id: 'CALIZA_WALLET',
  name: 'CALIZA_WALLET',
  currency: CryptoCurrencyCodeType.USDT,
  chain: 'Polygon',
  walletAddress: '',
};

export const revenueEarningsUSDC: CryptoAccount = {
  id: 'REVENUE_EARNINGS',
  name: 'REVENUE_EARNINGS',
  currency: CryptoCurrencyCodeType.USDC,
  chain: 'Polygon',
  walletAddress: '',
};

export class FiatAccount {
  public static empty(): FiatAccount {
    return {
      id: '',
      name: '',
      beneficiaryId: '',
      belongsToBeneficiary: false,
      fiatAccountType: FiatAccountType.unset,
      accountDetails: {
        pixDetails: {
          pixKey: '',
        },
      },
      nickname: null,
    };
  }

  public static toSimplifiedAccount(account: FiatAccount): SimplifiedAccount {
    if (!isAccountDetails(account.accountDetails)) {
      throw new Error('Unsupported account details type');
    }

    return match<AccountDetails, SimplifiedAccount>(account.accountDetails)
      .when(
        isWireAccountDetails,
        (details: WireAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.USD,
          bankName: details.wireDetails.bankInfo.name,
          country: toCountryType(details.wireDetails.bankInfo.country),
        }),
      )
      .when(
        isACHAccountDetails,
        (details: ACHAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.USD,
          bankName: details.achDetails.bankInfo.name,
          country: toCountryType(details.achDetails.bankInfo.country),
        }),
      )
      .when(
        isPIXAccountDetails,
        (details: PIXAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.BRL,
          bankName: details.pixDetails.pixKey,
          country: CountryType.BR,
        }),
      )
      .when(
        isSwiftAccountDetails,
        (details: SwiftAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.USD,
          bankName: details.swiftDetails.bankInfo.name,
          country: toCountryType(details.swiftDetails.bankInfo.country),
        }),
      )
      .when(
        isCOPAccountDetails,
        (details: COPAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.COP,
          bankName: details.copDetails.bankAccount,
          country: CountryType.CO,
        }),
      )
      .when(
        isSpeiAccountDetails,
        (details: SpeiAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.MXN,
          bankName: details.speiDetails.bankCode,
          country: CountryType.MX,
        }),
      )
      .when(
        isCVUAccountDetails,
        (details: CVUAccountDetails): SimplifiedAccount => ({
          id: account.id,
          name: account.name,
          currency: CurrencyCodeType.ARS,
          bankName: details.cvuDetails.cvu,
          country: CountryType.AR,
        }),
      )
      .exhaustive();
  }

  public static getAccountType(account: FiatAccount): TranslatableFiatAccountType {
    return match<FiatAccountType, TranslatableFiatAccountType>(account.fiatAccountType)
      .with(FiatAccountType.ach, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_ACH' as const)
      .with(FiatAccountType.swift, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_SWIFT' as const)
      .with(FiatAccountType.cnaps, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_CNAPS' as const)
      .with(FiatAccountType.chats, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_CHATS' as const)
      .with(FiatAccountType.sepa, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_SEPA' as const)
      .with(FiatAccountType.wire, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_WIRE' as const)
      .with(FiatAccountType.rtp, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_RTP' as const)
      .with(FiatAccountType.pix, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_PIX' as const)
      .with(FiatAccountType.cop, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_COP' as const)
      .with(FiatAccountType.spei, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_SPEI' as const)
      .with(FiatAccountType.cvu, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_CVU' as const)
      .with(
        FiatAccountType.crypto,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_CRYPTO' as const,
      )
      .with(
        FiatAccountType.fasterPayments,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_FASTER_PAYMENTS' as const,
      )
      .with(FiatAccountType.imps, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_IMPS' as const)
      .with(
        FiatAccountType.instapay,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_INSTAPAY' as const,
      )
      .with(FiatAccountType.rtc, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_RTC' as const)
      .with(
        FiatAccountType.promptpay,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_PROMPTPAY' as const,
      )
      .with(
        FiatAccountType.biFast,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_BI_FAST' as const,
      )
      .with(
        FiatAccountType.ghipss,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_GHIPSS' as const,
      )
      .with(
        FiatAccountType.pesalink,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_PESALINK' as const,
      )
      .with(FiatAccountType.fast, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_FAST' as const)
      .with(FiatAccountType.eft, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_EFT' as const)
      .with(FiatAccountType.tef, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_TEF' as const)
      .with(FiatAccountType.nip, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_NIP' as const)
      .with(FiatAccountType.cce, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_CCE' as const)
      .with(FiatAccountType.ipp, (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_IPP' as const)
      .with(
        FiatAccountType.bobAch,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_BOB_ACH' as const,
      )
      .with(
        FiatAccountType.mobileWallet,
        (): TranslatableFiatAccountType => 'FIAT_ACCOUNT_MOBILE_WALLET' as const,
      )
      .with(FiatAccountType.unset, (): TranslatableFiatAccountType => '' as const)
      .exhaustive();
  }

  public static getAccountDetails(account: FiatAccount): AccountDetails {
    return account.accountDetails;
  }
}

export interface SimplifiedAccount {
  readonly id: string;
  readonly currency: string;
  readonly name: string;
  readonly bankName?: string;
  readonly country: CountryType;
}

export interface ContactBase {
  readonly id: string | null;
  readonly entity: ContactData;
  readonly country: CountryType;
  readonly address: ContactAddress;
}

export interface Contact extends ContactBase {
  readonly id: string;
  readonly accounts: readonly FiatAccount[];
  readonly recipients: readonly RecipientResponse[];

  readonly belongsToBeneficiary: boolean;
}

export interface SelfContact extends ContactBase {
  readonly id: null;
}

export interface SearchableContact {
  readonly id: string;
  readonly displayName: string;
}

export const isAccountDetails = (something: unknown): something is AccountDetails =>
  isWireAccountDetails(something) ||
  isACHAccountDetails(something) ||
  isPIXAccountDetails(something) ||
  isSwiftAccountDetails(something) ||
  isCOPAccountDetails(something) ||
  isSpeiAccountDetails(something) ||
  isCVUAccountDetails(something);

export const isContactData = (something: unknown): something is ContactData =>
  isIndividualContact(something) || isCompanyContact(something);

const getContactName = (contact: Contact): string => {
  const { entity } = contact;

  if (isIndividualContact(entity)) {
    return `${entity.firstName} ${entity.lastName}`;
  } else if (isCompanyContact(entity)) {
    return entity.legalEntityName;
  } else {
    return 'Unsupported contact type';
  }
};

export class SelfContact {
  public static fromCustomer(customer: Customer): SelfContact {
    // `businessInfo` is null or undefined for users
    // that have not completed KYB.
    const legalEntityName = customer.businessInfo?.name ?? '';
    const businessAddress = customer.businessInfo?.address;

    return {
      id: null,
      entity: {
        type: ContactType.company,
        legalEntityName: legalEntityName,
        businessRelationship: BusinessRelationshipType.unset,
      },
      address: businessAddress
        ? {
            street: businessAddress.streetAddress ?? '',
            complement: businessAddress.complement ?? '',
            city: businessAddress.city ?? '',
            state: businessAddress.state ?? '',
            zipCode: businessAddress.zipCode?.toString() ?? '',
          }
        : ContactAddress.empty(),
      country: (customer.businessInfo?.country as CountryType) ?? CountryType.unset,
    };
  }
}

export class Contact {
  public static empty(): Contact {
    return {
      id: '',
      entity: {
        type: ContactType.individual,
        firstName: '',
        lastName: '',
        businessRelationship: BusinessRelationshipType.unset,
      },
      country: CountryType.unset,
      address: ContactAddress.empty(),
      accounts: [],
      recipients: [],
      belongsToBeneficiary: false,
    };
  }

  public static getName(contact: Contact): string {
    switch (contact.entity.type) {
      case ContactType.individual:
        return `${contact.entity.firstName} ${contact.entity.lastName}`;
      case ContactType.company:
        return contact.entity.legalEntityName;
      default:
        throw new Error('Invalid contact type');
    }
  }

  public static toRecipientAccounts(
    _locale: Locale,
    _banksByCountry: Partial<Record<CountryType, Record<string, string>>>,
    contact: Contact,
    _displayUsdcAsUsd: boolean = false,
  ): readonly RecipientAccount[] {
    const { recipients } = contact;

    return recipients.map((recipient: RecipientResponse): RecipientAccount => {
      return {
        id: recipient.id,
        name: getContactName(contact),
        bankName: getRecipientBankName(recipient) ?? null,
        accountNumber: getRecipientAccountNumber(recipient) ?? null,
        paymentMethod: recipient.type,
        currency: getRecipientCurrency(recipient),
        currencyCode: getRecipientCurrency(recipient),
        nickname: recipient.nickname ?? null,
      };
    });
  }
}

export interface PaymentMethodBase {
  readonly type: PaymentMethodAccountType;
}

export enum CryptoAssetType {
  unset = '',
}

export enum CryptoChain {
  unset = '',
}

export interface FiatPaymentACHWireFormBase {
  readonly type: PaymentMethodAccountType.fiat;
  readonly name: string;

  readonly country: CountryType.US;
  readonly currency: string;
  readonly paymentMethod: FiatAccountType;
  readonly bankName: string;
  readonly address: ContactAddress;
  readonly routingNumber: string;
  readonly accountNumber: string;
}

export interface CryptoPaymentForm extends PaymentMethodBase {
  readonly type: PaymentMethodAccountType.crypto;
  readonly name: string;

  readonly country: CountryType;
  readonly currency: string;
  readonly assetType: CryptoAssetType;
  readonly chain: CryptoChain;
  readonly walletAddress: string;
  readonly destination: string;
}

export enum BusinessRelationshipType {
  supplier = 'SUPPLIER',
  customer = 'CUSTOMER',
  partner = 'PARTNER',
  distributor = 'DISTRIBUTOR',
  consultant = 'CONSULTANT',
  affiliate = 'AFFILIATE',
  investor = 'INVESTOR',
  franchisee = 'FRANCHISEE',
  franchisor = 'FRANCHISOR',
  contractor = 'CONTRACTOR',
  subcontractor = 'SUBCONTRACTOR',
  strategicPartner = 'STRATEGIC_PARTNER',
  reseller = 'RESELLER',
  agent = 'AGENT',
  client = 'CLIENT',
  broker = 'BROKER',
  licensee = 'LICENSEE',
  licensor = 'LICENSOR',

  unset = '',
}

export const businessRelationshipTypeLabels: Record<BusinessRelationshipType, string> = {
  [BusinessRelationshipType.supplier]: 'Supplier',
  [BusinessRelationshipType.customer]: 'Customer',
  [BusinessRelationshipType.partner]: 'Partner',
  [BusinessRelationshipType.distributor]: 'Distributor',
  [BusinessRelationshipType.consultant]: 'Consultant',
  [BusinessRelationshipType.affiliate]: 'Affiliate',
  [BusinessRelationshipType.investor]: 'Investor',
  [BusinessRelationshipType.franchisee]: 'Franchisee',
  [BusinessRelationshipType.franchisor]: 'Franchisor',
  [BusinessRelationshipType.contractor]: 'Contractor',
  [BusinessRelationshipType.subcontractor]: 'Subcontractor',
  [BusinessRelationshipType.strategicPartner]: 'Strategic Partner',
  [BusinessRelationshipType.reseller]: 'Reseller',
  [BusinessRelationshipType.agent]: 'Agent',
  [BusinessRelationshipType.client]: 'Client',
  [BusinessRelationshipType.broker]: 'Broker',
  [BusinessRelationshipType.licensee]: 'Licensee',
  [BusinessRelationshipType.licensor]: 'Licensor',
  // Shouldn't have a label
  [BusinessRelationshipType.unset]: '',
};

export interface ContactFormBase {
  readonly type: ContactType;
  readonly country: CountryType;
  readonly address: ContactAddress;
  readonly businessRelationship: BusinessRelationshipType;
}

export interface IndividualContactForm extends ContactFormBase {
  readonly type: ContactType.individual;
  readonly firstName: string;
  readonly lastName: string;
}

export interface CompanyContactForm extends ContactFormBase {
  readonly type: ContactType.company;
  readonly legalEntityName: string;
}

export type ContactForm = IndividualContactForm | CompanyContactForm;
